import { Fragment } from 'react';

import { InboxOutlined, TagOutlined } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';

import { TiendaPerfectaStopMaterials } from 'models/TiendaPerfecta';

import {
  StopMaterialGroup,
  StopMaterialRow,
  getStopMaterialTotals,
  groupStopMaterials,
} from '../utils/stopDetail';

const I18N_BASE = 'scenes.route_report.components.stop_materials';

interface StopMaterialsTableProps {
  materials: TiendaPerfectaStopMaterials;
}

/**
 * Lo sugerido al lado de lo implementado, agrupado por paquete.
 *
 * DOS columnas y ninguna resta. Lo sugerido no es una meta que haya que
 * cumplir: en terreno se puede dejar más de lo sugerido, menos, o material que
 * nadie sugirió. Una columna "faltante" convertiría una diferencia normal en un
 * incumplimiento, que es una lectura que el contrato no respalda.
 *
 * El orden lo fija el servidor —paquetes primero, con sus líneas juntas, y los
 * sueltos al final— y es el mismo en los dos arreglos justamente para poder
 * leerse en paralelo. Acá no se reordena nada.
 */
function StopMaterialsTable({ materials }: StopMaterialsTableProps) {
  const { t } = useTranslation();

  const groups = groupStopMaterials(materials);

  if (!groups.length) {
    return (
      <div className="stop-empty stop-empty--muted">
        {t(`${I18N_BASE}.empty`)}
      </div>
    );
  }

  const totals = getStopMaterialTotals(materials);

  const renderRow = (
    row: StopMaterialRow,
    index: number,
    modifier?: string
  ) => {
    return (
      <tr key={`${row.materialId}-${index}`} className={modifier}>
        <td>
          <span className="stop-materials__name">{row.materialName}</span>
          <span className="stop-materials__sku">{row.materialSku}</span>
        </td>
        {/* Un cero se escribe como raya: "0 implementado" y "no aplica" se leen
            igual en una celda numérica, y la raya no finge una cuenta. */}
        <td className={row.suggested ? '' : 'stop-materials__none'}>
          {row.suggested || '—'}
        </td>
        <td className={row.delivered ? '' : 'stop-materials__none'}>
          {row.delivered || '—'}
        </td>
      </tr>
    );
  };

  /**
   * Tres niveles de jerarquía y UNA sola cabecera de columnas.
   *
   *   SECCIÓN     Paquetes · Material suelto
   *     paquete     Kit Góndola Nocturna · × 1 paquete
   *       material    Stopper LED de góndola      3   3
   *
   * La sección es lo que faltaba: sin ella, "Kit Góndola Nocturna" y "Material
   * suelto" se veían idénticos, cuando uno es UN paquete y el otro es toda una
   * categoría. Y los materiales van indentados bajo su paquete, que es lo que
   * hace legible a cuál pertenecen.
   *
   * La cabecera de columnas no se repite por sección: con una tabla por grupo,
   * "Material · Sugerido · Implementado" salía tres veces en una parada con dos
   * kits, y cada tabla calculaba sus propios anchos, así que las columnas ni
   * siquiera quedaban alineadas entre sí.
   */
  const sectionRow = (label: string, icon: JSX.Element) => (
    <tr className="stop-materials__section-row">
      <td colSpan={3}>
        <span className="stop-materials__section">
          {icon}
          {label}
        </span>
      </td>
    </tr>
  );

  const packageRow = (group: StopMaterialGroup) => (
    <tr className="stop-materials__group-row" key={`head-${group.key}`}>
      <td colSpan={3}>
        <span className="stop-materials__group-head">
          <span className="stop-materials__group-name">
            {group.packageName}
          </span>
          <span className="stop-materials__group-sku">{group.packageSku}</span>
          {group.packageQuantity !== null && (
            <span className="stop-materials__group-qty">
              {/* `count` es la variable que i18next usa para el plural: sin
                  ella no resuelve `_one`/`_other`. */}
              {t(`${I18N_BASE}.packages`, { count: group.packageQuantity })}
            </span>
          )}
        </span>
      </td>
    </tr>
  );

  const packageGroups = groups.filter((g) => g.sourceType === 'package');
  const looseGroups = groups.filter((g) => g.sourceType !== 'package');

  return (
    <div className="stop-materials">
      <table>
        <thead>
          <tr>
            <th>{t(`${I18N_BASE}.columns.material`)}</th>
            <th>{t(`${I18N_BASE}.columns.suggested`)}</th>
            <th>{t(`${I18N_BASE}.columns.delivered`)}</th>
          </tr>
        </thead>

        {packageGroups.length > 0 && (
          <tbody className="stop-materials__group">
            {sectionRow(t(`${I18N_BASE}.packages_section`), <InboxOutlined />)}
            {packageGroups.map((group) => (
              <Fragment key={group.key}>
                {packageRow(group)}
                {group.rows.map((row, index) =>
                  renderRow(row, index, 'stop-materials__row--in-package')
                )}
              </Fragment>
            ))}
          </tbody>
        )}

        {looseGroups.length > 0 && (
          <tbody className="stop-materials__group">
            {sectionRow(t(`${I18N_BASE}.loose_group`), <TagOutlined />)}
            {/* El callback se envuelve en vez de pasar `renderRow` directo:
                `Array.map` entrega (valor, índice, ARRAY) y ese tercer
                argumento chocaría con el `modifier` opcional de la firma. */}
            {looseGroups
              .flatMap((group) => group.rows)
              .map((row, index) => renderRow(row, index))}
          </tbody>
        )}
      </table>

      <div className="stop-materials__foot">
        <span>
          {t(`${I18N_BASE}.totals`, {
            suggested: totals.suggested,
            delivered: totals.delivered,
          })}
        </span>
      </div>
    </div>
  );
}

export default StopMaterialsTable;
